'use client';

/**
 * i18n 核心（2026-09-16 多語種全面設置，複刻 couponly 零依賴架構）
 * <LocaleProvider> + useI18n() + useT()；字典為扁平鍵（'nav.arsenal'），
 * 保留 t('key') 呼叫風格以最小化遷移差異，同時保留 Widen 型別基線：
 * 其他語言字典型別必須為 Dict（缺鍵 = 編譯錯誤）+ i18n:check runtime 雙保險。
 *
 * locale 解析優先序（jkd 特有調整）：
 *   路徑前綴（/en/、/zh-CN/，配合靜態預渲染語言目錄）→ URL ?lang=
 *   → localStorage（含舊 'jkd-language' 鍵一次性遷移）→ navigator.language → zh-HK
 *
 * C 階段（2026-10-10）：本檔標 'use client'（LocaleProvider hooks）；
 * 純值（DICT_BY_CODE／resolveLocale 等）拆到 ./dicts——server 端（seo.ts）由嗰邊 import。
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_LOCALE, LOCALES, LOCALE_STORAGE_KEY, LEGACY_STORAGE_KEY, LEGACY_LANG_MAP } from './locales';
import { DICT_BY_CODE, META_BY_CODE, resolveLocale, mapNavigatorLang } from './dicts';

// 純值單一來源在 ./dicts；呢度 re-export 保持舊 import 路徑（Vite 版 App/prerender entry）不變
export { DICT_BY_CODE, META_BY_CODE, resolveLocale } from './dicts';
export type { Dict } from './dicts';
import type { Dict } from './dicts';

export type DictKey = keyof Dict;

export interface I18nCtx {
  locale: string;
  setLocale: (code: string) => void;
  t: (key: DictKey) => string;
}

const Ctx = createContext<I18nCtx | null>(null);

/** 路徑中的 locale 段 → locale（/en/、/zh-CN/、/ms/、/ar/、/es/，含子頁如 /diagnosis/en/；其餘 null） */
function localeFromPath(pathname: string): string | null {
  const m = pathname.match(/\/(en|zh-CN|ms|ar|es)(\/|$)/);
  return m ? m[1] : null;
}

/** 初始 locale（優先序見檔頭）；?lang=xx 寫入 localStorage 後即場清走 URL 參數 */
function resolveInitialLocale(): string {
  try {
    // 0) 靜態預渲染語言目錄（/en/、/zh-CN/）——與落地的 prerender HTML 保持一致
    const fromPath = localeFromPath(window.location.pathname);
    if (fromPath) return fromPath;
    // 1) URL ?lang=
    const qs = new URLSearchParams(window.location.search);
    const qLang = qs.get('lang');
    if (qLang) {
      qs.delete('lang');
      const rest = qs.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? '?' + rest : '') + window.location.hash);
      const r = resolveLocale(qLang);
      if (r) {
        localStorage.setItem(LOCALE_STORAGE_KEY, r);
        return r;
      }
    }
    // 2) 新 storage key，附舊 'jkd-language'（'zh'|'en'）一次性遷移
    const saved = resolveLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
    if (saved) return saved;
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy && LEGACY_LANG_MAP[legacy]) {
      localStorage.setItem(LOCALE_STORAGE_KEY, LEGACY_LANG_MAP[legacy]);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      return LEGACY_LANG_MAP[legacy];
    }
    // 3) navigator.language 對映
    const nav = mapNavigatorLang(navigator.language);
    return DICT_BY_CODE[nav] ? nav : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE; // SSR/奇怪環境兜底
  }
}

export function LocaleProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: string }) {
  const [locale, setLocaleState] = useState<string>(initialLocale ?? resolveInitialLocale);

  // <html lang/dir> + 文件標題 + meta description 跟 locale 走
  useEffect(() => {
    const meta = META_BY_CODE[locale] ?? LOCALES[0];
    document.documentElement.lang = meta.htmlLang;
    document.documentElement.dir = meta.dir;
    document.title = meta.dict['meta.title'];
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', meta.dict['meta.description']);
  }, [locale]);

  const value = useMemo<I18nCtx>(
    () => ({
      locale,
      setLocale: (code: string) => {
        const r = resolveLocale(code);
        if (!r) return;
        localStorage.setItem(LOCALE_STORAGE_KEY, r);
        setLocaleState(r);
      },
      t: (key: DictKey) => {
        const dict = DICT_BY_CODE[locale] ?? DICT_BY_CODE[DEFAULT_LOCALE];
        return dict[key] ?? DICT_BY_CODE[DEFAULT_LOCALE][key] ?? key;
      },
    }),
    [locale]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useI18n/useT must be used within <LocaleProvider>');
  return c;
}

/** t('key') 風格的全站文案取用（保留舊 languageStore 呼叫簽名，遷移只改 import） */
export function useT(): (key: DictKey) => string {
  return useI18n().t;
}
